/**
 * USE-WORK-ORDER-DATA.JS - Work Order Data Fetching and Management Hook
 *
 * SUMMARY:
 * Custom hook that manages data fetching for work order creation/editing.
 * Handles project data loading, vendor list fetching, and existing WO data retrieval.
 */

import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resolveProjectType } from '../../utils/resolve-project-type';
import idx from 'idx';
import { errorSnackBar } from '../../../../actions/common.action';
import { getVendorList } from '../../../../actions/vendor-management.action';
import {
  getProject,
  getProjectList as getProjectListAPI,
  getPurchaseOrder,
  getProjectSummary
} from '../../../../actions/project-management.action';
import { getOptionsData } from '../../utils/get-options';
import { combineData, sortByKeyAsc } from '../../../../util/common';
import { convertToReactSelectFormat } from '../../../../util/section-dropdown-format';
import {
  collectTasksFromProjectData,
  buildAttributionOptions,
  removeAttributionFlags,
} from "../utils/work-order-helpers";

/**
 * Custom hook for managing work order data fetching and state.
 *
 * @param {Object} params - Hook parameters
 * @param {Function} params.setFields - Function to update form fields
 * @param {Function} params.setProjectType - Function to update project type
 * @param {Function} params.setProjectName - Function to update project name
 * @returns {Object} Data state and fetching functions
 *
 * @example
 * const {
 *   vendorsList,
 *   allTasks,
 *   loadProjectTasks,
 *   fetchWorkOrderData
 * } = useWorkOrderData({ setFields, setProjectType, setProjectName });
 */
export const useWorkOrderData = ({
  setFields,
  setProjectType,
  setProjectName,
}) => {
  const hasContractorWork = (subcontractorTotalCost) =>
    parseFloat(subcontractorTotalCost || 0) > 0;
  const dispatch = useDispatch();
  const projectState = useSelector(state => state.project);
  const taskByIdRef = useRef(new Map());
  const [vendorsList, setVendorsList] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [attributionDropdownOptions, setAttributionDropdownOptions] = useState(
    [],
  );
  const [originalSectionData, setOriginalSectionData] = useState([]);

  /**
   * Combines project and variation data into unified section structure.
   * Creates a "Variations" section for approved variations.
   *
   * @param {Object} project - Project data
   * @param {Array} options - Variation/option data
   * @returns {Array} Combined project data with variations
   */
  const setOptionsToSections = (project, options) => {
    const _options = options || [];
    let variations = {
      name: "Variations",
      id: 1,
      parent_stage_id: null,
      stages: null,
      tasks: [],
    };

    if (Array.isArray(_options) && _options.length) {
      _options.forEach((option) => {

        // Include both PENDING and APPROVED variations (same as PO)
        if (
          (option.proposal_status === "APPROVED" ||
            option.proposal_status === "PENDING") &&
          !option.stageId
        ) {
          const _option = {
            ...option,
            optionProposalStatus: option.proposal_status || option.optionProposalStatus
          };
          _option.name = `${option.variationNo || `V-${String(option.variationNumber).padStart(3, "0")}`} - ${option.name}`;
          variations.tasks.push(_option);
        }
      });
    }

    const projectData = combineData({ project, variation: _options });
    Array.isArray(projectData) && projectData.push(variations);
    return projectData;
  };

  /**
   * Loads project tasks and builds task map for dropdown and expansion.
   * Applies attribution flag removal for maximum visibility while preserving
   * original data for attribution logic.
   *
   * @param {string} projectId - Project ID to load tasks for
   * @param {Array<string>} alwaysIncludeTaskIds - Task IDs to always include (for editing)
   * @returns {Promise<{allTasks: Array, attributionOptions: Array}>}
   */
  const loadProjectTasks = async (projectId, alwaysIncludeTaskIds = []) => {
    if (!projectId || projectId === 'undefined') return { allTasks: [], attributionOptions: [] };
    try {
      const [project, projectSummary] = await Promise.all([
        dispatch(getProject(projectId)),
        dispatch(getProjectSummary(projectId))
      ]);
      setProjectType(resolveProjectType(projectSummary?.projectType, projectState));
      setProjectName(idx(project, _ => _.name) || '');
      const data = await getOptionsData({ projectId, dispatch });
      const projectData = setOptionsToSections(project, data);

      // Build taskById map for subcontractor item expansion
      taskByIdRef.current = collectTasksFromProjectData(projectData);

      const rawTasks = Array.from(taskByIdRef.current.values()).map((task) => ({
        value: task.id,
        subcontractorTotal_Cost: task.subcontractorTotal_Cost || 0,
        hasContractorWork: hasContractorWork(task.subcontractorTotal_Cost),
      }));

      // WO Creation: Show all tasks (no filtering) but handle attribution automatically
      const filteredData = removeAttributionFlags(projectData || []);

      // Store original section data (with flags intact) for attribution checking
      setOriginalSectionData(projectData || []);

      // Build attribution dropdown options from original section data
      const attributionOptions = buildAttributionOptions(projectData || []);

      const formattedTasksRaw = convertToReactSelectFormat(
        filteredData,
        null,
        true,
      );
      const formattedTasks = formattedTasksRaw.map((task) => {
        const matched = rawTasks.find((t) => t.value === task.value);
        return {
          ...task,
          subcontractorTotal_Cost: matched?.subcontractorTotal_Cost || 0,
          hasContractorWork: matched?.hasContractorWork || false,
        };
      });

      // Update form field with selected project
      setFields((prev) => ({
        ...prev,
        project: { ...prev.project, value: projectId },
      }));

      setAllTasks([
        { label: "Select All", value: "select_all" },
        ...formattedTasks,
      ]);
      setAttributionDropdownOptions(attributionOptions);

      // Set project name and type from fetched data as fallback/source of truth
      if (project) {
        if (project.projectName || project.name) setProjectName(project.projectName || project.name);
        if (project.projectDataType || project.projectType || project.type) setProjectType(project.projectDataType || project.projectType || project.type);
      }

      return { allTasks: formattedTasks, attributionOptions };
    } catch (error) {
      dispatch(errorSnackBar("Failed to load project tasks"));
    }
  };

  /**
   * Fetches existing work order data for editing.
   * Loads WO fields and items, building selectedTasks and selectedItems.
   *
   * @param {string} poId - Purchase order/work order ID
   * @param {string} projectId - Project ID (optional, from URL)
   * @param {Function} projectChangeHandler - Handler for project change
   * @returns {Promise<Object|null>} PO data or null if failed
   */
  const fetchWorkOrderData = async (poId, projectId, projectChangeHandler) => {
    if (!poId || poId === 'undefined') return null;
    try {
      const query = `?poId=${poId}`;
      const poData = await dispatch(getPurchaseOrder(query));

      if (!poData) {
        dispatch(errorSnackBar("Failed to fetch Work Order data"));
        return null;
      }

      // Update form fields
      setFields((prev) => ({
        ...prev,
        quote_no: { ...prev.quote_no, value: poData.quoteNo || "" },
        vendor: { ...prev.vendor, value: poData.vendorId || "" },
        street_address: {
          ...prev.street_address,
          value: poData.street_address || "",
        },
        suburb: { ...prev.suburb, value: poData.suburb || "" },
        state: { ...prev.state, value: poData.state || "" },
        postcode: { ...prev.postcode, value: poData.postcode || "" },
        country: { ...prev.country, value: poData.country || "" },
      }));

      // Load project tasks
      const selectedTaskIds = Array.isArray(poData.items)
        ? poData.items.map((item) => item.taskId)
        : [];

      if (!projectId && poData.projectId) {
        setFields((prev) => ({
          ...prev,
          project: { ...prev.project, value: poData.projectId || "" },
        }));
        await projectChangeHandler(poData.projectId, selectedTaskIds);
      } else if (projectId) {
        await loadProjectTasks(projectId, selectedTaskIds);
      }

      return poData;
    } catch (error) {
      dispatch(errorSnackBar("Failed to fetch Work Order data"));
      return null;
    }
  };

  /**
   * Fetches vendor list and updates form field options.
   *
   * @returns {Promise<void>}
   */
  const initializeVendors = async () => {
    try {
      const vendorsListData = await dispatch(getVendorList("sub-contractor"));
      const vendors =
        (vendorsListData || []).map(({ id, businessName }) => ({
          key: businessName,
          value: id,
        })) || [];

      setFields((prev) => ({
        ...prev,
        vendor: {
          ...prev.vendor,
          items: vendors,
        },
      }));

      setVendorsList(vendorsListData || []);
    } catch (error) {
      dispatch(errorSnackBar("Failed to load vendor list"));
    }
  };

  /**
   * Fetches project list and updates form field options.
   * Only called when not in project context (no projectId in URL).
   *
   * @returns {Promise<void>}
   */
  const initializeProjects = async () => {
    try {
      const list = await dispatch(getProjectListAPI(null, "PROJECT"));
      let projects = (list || []).map(({ name, id }) => ({
        key: name.trim(),
        value: id,
      }));
      projects = sortByKeyAsc(projects);

      setFields((prev) => ({
        ...prev,
        project: { ...prev.project, items: projects },
      }));
    } catch (error) {
      dispatch(errorSnackBar("Failed to fetch project list"));
    }
  };

  return {
    vendorsList,
    allTasks,
    attributionDropdownOptions,
    taskByIdRef,
    originalSectionData,
    loadProjectTasks,
    fetchWorkOrderData,
    initializeVendors,
    initializeProjects,
    setAllTasks,
    setAttributionDropdownOptions,
  };
};
